import { Loader2, X } from 'lucide-react';

// Peças visuais da dashboard do Controlly, iguais às de lá: use estas em vez de repetir classes.
export const cardClass = 'rounded-3xl border border-neutral-200 bg-white dark:border-white/[0.06] dark:bg-[#0e0e10]';
export const inputClass = 'h-10 w-full rounded-xl border border-neutral-200 bg-white px-3 text-sm text-neutral-900 placeholder:text-neutral-400 focus:border-emerald-500 focus:outline-none disabled:opacity-60 dark:border-white/[0.08] dark:bg-[#050505] dark:text-neutral-100';

export function Page({ children, className = '' }) {
  return <div className={`mx-auto flex w-full max-w-[1400px] flex-col gap-4 p-4 sm:p-6 lg:p-8 ${className}`}>{children}</div>;
}

export function PageHeader({ title, description, children }) {
  return (
    <div className="flex flex-col gap-4 pb-2 sm:flex-row sm:items-end sm:justify-between">
      <div>
        <h1 className="text-2xl font-medium tracking-tight text-neutral-900 sm:text-[28px] dark:text-neutral-100">{title}</h1>
        {description && <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400">{description}</p>}
      </div>
      {children && <div className="flex flex-wrap items-center gap-2">{children}</div>}
    </div>
  );
}

export function Card({ children, className = '', ...props }) {
  return <div className={`${cardClass} ${className}`} {...props}>{children}</div>;
}

export function CardTitle({ children, right }) {
  return (
    <div className="flex items-baseline justify-between gap-4">
      <p className="text-[15px] text-neutral-500 dark:text-neutral-400">{children}</p>
      {right}
    </div>
  );
}

// Número grande com rótulo e uma linha de apoio, como os cards de resumo da dashboard
export function Stat({ label, value, hint, icon: Icon, tone = 'neutral', loading = false }) {
  const tones = {
    neutral: 'bg-neutral-100 text-neutral-500 dark:bg-white/[0.05] dark:text-neutral-400',
    green: 'bg-emerald-500/10 text-emerald-500',
    blue: 'bg-sky-500/10 text-sky-500',
    amber: 'bg-amber-500/10 text-amber-500',
    red: 'bg-red-500/10 text-red-500',
  };
  return (
    <Card className="p-5">
      <div className="flex items-start justify-between gap-3">
        <p className="text-[15px] text-neutral-500 dark:text-neutral-400">{label}</p>
        {Icon && <span className={`flex size-9 shrink-0 items-center justify-center rounded-full ${tones[tone]}`}><Icon className="size-4" /></span>}
      </div>
      {loading
        ? <Skeleton className="mt-3 h-8 w-28" />
        : <p className="mt-2 text-[28px] font-medium leading-none tracking-[-0.03em] text-neutral-900 dark:text-white">{value}</p>}
      {hint && <p className="mt-2 text-xs text-neutral-500">{hint}</p>}
    </Card>
  );
}

export function Button({ variant = 'primary', loading = false, icon: Icon, children, className = '', ...props }) {
  const styles = {
    primary: 'bg-emerald-500 text-white hover:bg-emerald-600 dark:text-black',
    secondary: 'border border-neutral-200 text-neutral-700 hover:bg-neutral-50 dark:border-white/[0.08] dark:text-neutral-200 dark:hover:bg-white/[0.04]',
    danger: 'bg-red-500 text-white hover:bg-red-600',
    ghost: 'text-neutral-500 hover:bg-neutral-100 hover:text-neutral-900 dark:text-neutral-400 dark:hover:bg-white/[0.05] dark:hover:text-neutral-100',
  };
  return (
    <button
      className={`inline-flex h-10 items-center justify-center gap-1.5 rounded-xl px-4 text-sm font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${styles[variant]} ${className}`}
      disabled={loading || props.disabled}
      {...props}
    >
      {loading ? <Loader2 className="size-4 animate-spin" /> : Icon ? <Icon className="size-4" /> : null}
      {children}
    </button>
  );
}

export function IconButton({ icon: Icon, label, tone = 'neutral', className = '', ...props }) {
  const tones = {
    neutral: 'hover:bg-neutral-100 hover:text-neutral-900 dark:hover:bg-white/[0.06] dark:hover:text-neutral-100',
    danger: 'hover:bg-red-500/10 hover:text-red-500',
  };
  return (
    <button type="button" title={label} aria-label={label} className={`flex size-8 items-center justify-center rounded-lg text-neutral-400 transition-colors disabled:cursor-not-allowed disabled:opacity-40 ${tones[tone]} ${className}`} {...props}>
      <Icon className="size-4" />
    </button>
  );
}

export function Segmented({ options, value, onChange }) {
  return (
    <div className="no-scrollbar inline-flex max-w-full gap-1 overflow-x-auto rounded-xl bg-neutral-100 p-1 dark:bg-white/[0.03]">
      {options.map(({ value: v, label, icon: Icon, count }) => (
        <button
          key={v}
          type="button"
          onClick={() => onChange(v)}
          className={`flex shrink-0 items-center gap-1.5 rounded-lg px-3.5 py-1.5 text-sm transition-colors ${
            value === v ? 'bg-white text-neutral-900 dark:bg-white/[0.08] dark:text-white' : 'text-neutral-500 hover:text-neutral-900 dark:text-neutral-400 dark:hover:text-neutral-100'
          }`}
        >
          {Icon && <Icon className="size-3.5" />}
          {label}
          {count !== undefined && <span className="text-xs text-neutral-400">{count}</span>}
        </button>
      ))}
    </div>
  );
}

// Selo pequeno de status (papel, assinatura...)
export function Badge({ children, tone = 'neutral' }) {
  const tones = {
    neutral: 'bg-neutral-100 text-neutral-600 dark:bg-white/[0.06] dark:text-neutral-300',
    green: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400',
    blue: 'bg-sky-500/10 text-sky-600 dark:text-sky-400',
    amber: 'bg-amber-500/10 text-amber-600 dark:text-amber-400',
    red: 'bg-red-500/10 text-red-600 dark:text-red-400',
  };
  return <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${tones[tone]}`}>{children}</span>;
}

// Iniciais num círculo, com tom fixo por nome
const AVATAR_TONES = [
  'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400',
  'bg-sky-500/15 text-sky-600 dark:text-sky-400',
  'bg-amber-500/15 text-amber-600 dark:text-amber-400',
  'bg-violet-500/15 text-violet-600 dark:text-violet-400',
  'bg-rose-500/15 text-rose-600 dark:text-rose-400',
  'bg-teal-500/15 text-teal-600 dark:text-teal-400',
];

export const initials = (name) => {
  const parts = (name || '').trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '?';
  return (parts.length === 1 ? parts[0][0] : parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

export function Avatar({ name, size = 'size-10' }) {
  const tone = AVATAR_TONES[((name || '?').charCodeAt(0) || 0) % AVATAR_TONES.length];
  return <span className={`flex ${size} shrink-0 items-center justify-center rounded-full text-sm font-semibold ${tone}`}>{initials(name)}</span>;
}

// Campo com rótulo em cima, como nos formulários da dashboard
export function Field({ label, hint, children }) {
  return (
    <label className="flex flex-col gap-1.5 text-sm text-neutral-600 dark:text-neutral-400">
      {label}
      {children}
      {hint && <span className="text-xs text-neutral-400 dark:text-neutral-500">{hint}</span>}
    </label>
  );
}

export function EmptyState({ icon: Icon, title, description, children }) {
  return (
    <div className="flex flex-col items-center justify-center gap-2 px-4 py-12 text-center">
      {Icon && <Icon className="size-8 text-neutral-300 dark:text-neutral-700" />}
      <p className="text-sm text-neutral-500 dark:text-neutral-400">{title}</p>
      {description && <p className="text-xs text-neutral-400 dark:text-neutral-500">{description}</p>}
      {children && <div className="mt-2">{children}</div>}
    </div>
  );
}

export function Modal({ title, onClose, children, footer, size = 'max-w-md' }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={onClose}>
      <div className={`chat-up max-h-[90vh] w-full overflow-y-auto ${size} ${cardClass} p-6`} onClick={(e) => e.stopPropagation()}>
        <div className="mb-4 flex items-center justify-between gap-4">
          <h3 className="text-lg font-medium tracking-tight text-neutral-900 dark:text-neutral-100">{title}</h3>
          <IconButton icon={X} label="Fechar" onClick={onClose} />
        </div>
        {children}
        {footer && <div className="mt-6 flex flex-wrap justify-end gap-2">{footer}</div>}
      </div>
    </div>
  );
}

// Confirmação de ação difícil de desfazer, mostrando o que vai acontecer
export function ConfirmModal({ title, children, confirmLabel = 'Confirmar', onConfirm, onClose, loading = false }) {
  return (
    <Modal
      title={title}
      onClose={onClose}
      footer={<>
        <Button variant="secondary" onClick={onClose} disabled={loading}>Cancelar</Button>
        <Button variant="danger" onClick={onConfirm} loading={loading}>{confirmLabel}</Button>
      </>}
    >
      <div className="text-sm text-neutral-600 dark:text-neutral-400">{children}</div>
    </Modal>
  );
}

// Bloco com brilho no lugar do dado enquanto carrega: a tela fica montada e só os dados piscam
export function Skeleton({ className = '' }) {
  return <span aria-hidden className={`skeleton block rounded-lg ${className}`} />;
}

// Linhas no formato das listas: avatar, duas linhas de texto e valor à direita
export function SkeletonRows({ rows = 4, className = '' }) {
  return (
    <div className={`flex flex-col divide-y divide-neutral-100 dark:divide-white/[0.05] ${className}`}>
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className="flex items-center gap-3 py-3">
          <Skeleton className="size-10 shrink-0 rounded-full" />
          <div className="flex flex-1 flex-col gap-2">
            <Skeleton className="h-3.5 w-2/5" />
            <Skeleton className="h-3 w-1/4" />
          </div>
          <Skeleton className="h-3.5 w-16 shrink-0" />
        </div>
      ))}
    </div>
  );
}

// Cabeçalho de coluna que ordena ao clicar
export function SortHeader({ label, active, order, onClick, className = '' }) {
  return (
    <th className={`px-4 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 ${className}`}>
      <button type="button" onClick={onClick} className="inline-flex items-center gap-1 hover:text-neutral-900 dark:hover:text-neutral-100">
        {label}
        <span className={active ? 'text-emerald-500' : 'text-transparent'}>{order === 'asc' ? '↑' : '↓'}</span>
      </button>
    </th>
  );
}
